import { useEffect } from 'react';

import { type UseAsyncDataResT, time } from '@dr.pogodin/react-utils';

import { getUserArtist } from 'services/user';

import {
  type ArtistT,
  VIDEO_STATUS_REFRESH_INTERVAL,
  useAsyncData,
  useGlobalState,
} from 'utils';

/**
 * If the authenticated user has the "ARTIST" or "PRACTITIONER_APPLICANT"
 * role it loads into the global state the corresponding Artist object, and
 * returns the envelope with this object; otherwise it loads "null" and
 * returns the envelope with that value.
 */
export default function useUserArtist(): UseAsyncDataResT<ArtistT> {
  const [user] = useGlobalState('user');

  const isPractitioner = user?.roles.includes('ARTIST')
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    || user?.roles.includes('PRACTITIONER_APPLICANT');

  const res = useAsyncData(
    'userArtist',
    (): Promise<ArtistT | null> | null => (
      isPractitioner && user?.id ? getUserArtist(user.id) : null
    ),
    { deps: [isPractitioner, user?.id], maxage: time.YEAR_MS },
  );

  const hasIntroVideo = !!res.data?.videoUrl;
  const isIntroVideoReady = !!res.data?.videoIsReadyToBePlayed;

  const reloadPractitioner = res.reload;

  useEffect(() => {
    let timerId: NodeJS.Timeout | undefined;

    if (hasIntroVideo && !isIntroVideoReady) {
      const iteration = () => {
        void (async () => {
          await reloadPractitioner();
          if (timerId !== undefined) {
            timerId = setTimeout(iteration, VIDEO_STATUS_REFRESH_INTERVAL);
          }
        })();
      };

      timerId = setTimeout(iteration, VIDEO_STATUS_REFRESH_INTERVAL);
    }

    return () => {
      clearTimeout(timerId);
      timerId = undefined;
    };
  }, [hasIntroVideo, isIntroVideoReady, reloadPractitioner]);

  return res;
}
